import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { School } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", organisation: "", email: "", phone: "", message: "" };
const GROUP_TIERS = [
  { name: "Holiday Group Workshop", text: "Small groups, hands-on on the decks. Perfect for youth programs over the school holidays.", border: "border-lime-300/40" },
  { name: "School Term Program", text: "Weekly sessions through the term. Music, wellbeing and creative skills for students.", border: "border-pink-400/40" },
  { name: "Council & Community Events", text: "Youth events, pop-up workshops and community days, tailored to your group.", border: "border-cyan-300/40" },
];

const FIELDS = [
  ["full_name", "Your name", "text"],
  ["organisation", "School / council / organisation", "text"],
  ["email", "Email", "email"],
  ["phone", "Phone", "tel"],
];

export const SchoolsEnquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (FIELDS.some(([k]) => !form[k].trim())) {
      toast.error("Please fill in all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, message: form.message || "Junior DJ Academy program enquiry", enquiry_type: "junior_academy_school" });
      setSent(true);
      setForm(EMPTY);
      toast.success("Thanks! We'll be in touch shortly.");
    } catch {
      toast.error("Couldn't send, please try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="py-20 md:py-28 bg-dream-bg" data-testid="jr-schools">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
        <div>
          <div className="w-14 h-14 rounded-2xl bg-cyan-300 flex items-center justify-center mb-6"><School className="w-7 h-7 text-black" /></div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-cyan-300 mb-4">Schools &amp; councils</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white tracking-tight">Bring the Academy to your students.</h2>
          <p className="text-white/60 mt-4 leading-relaxed">School programs, council youth initiatives and community groups are encouraged. We tailor group workshops and holiday programs that keep young people engaged, connected and creative.</p>
          <div className="mt-8 space-y-3" data-testid="jr-group-tiers">
            {GROUP_TIERS.map((t) => (
              <div key={t.name} className={`p-5 rounded-2xl border ${t.border} bg-white/[0.03]`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-white font-display font-bold">{t.name}</p>
                    <p className="text-white/55 text-sm mt-1">{t.text}</p>
                  </div>
                  <span className="shrink-0 px-3 py-1 rounded-full bg-white text-black text-[11px] font-bold uppercase tracking-wide">Enquire for pricing</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="rounded-3xl p-7 md:p-8 bg-dream-surface border border-white/10 space-y-4" data-testid="jr-schools-form">
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}>
                <label className="text-white/70 text-sm">{label}</label>
                <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-dream-bg border-white/15 text-white" data-testid={`jr-school-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-white/70 text-sm">Tell us about your group (optional)</label>
            <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-dream-bg border-white/15 text-white" placeholder="Group size, ages, preferred dates…" data-testid="jr-school-message" />
          </div>
          <Button type="submit" disabled={sending} className="w-full bg-cyan-300 hover:bg-cyan-200 text-black rounded-full py-6 font-bold" data-testid="jr-school-submit">
            {sending ? "Sending…" : sent ? "Sent! Send another" : "Send Enquiry"}
          </Button>
        </form>
      </div>
    </section>
  );
};
